// src/components/MIRInboxPage.jsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getMyAssignedMIRs,
  acceptMIR,
  rejectMIR,

  // ✅ NEW: WIR APIs
  getMyAssignedWIRs,
  acceptWIR,
  rejectWIR,

  getProjectsForCurrentUser,
} from "../api";
import { projectInstance } from "../api/axiosInstance";
import { useTheme } from "../ThemeContext";

// Theme constants
const ORANGE = "#ffbe63";
const BG_OFFWHITE = "#fcfaf7";

export default function MIRInboxPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  // ✅ Dropdown: which list to show
  const [docType, setDocType] = useState("MIR"); // "MIR" | "WIR"

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [statusFilter, setStatusFilter] = useState("");
  const [projectIdFilter, setProjectIdFilter] = useState("");

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // action loading
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // 🔹 project id -> name map (from localStorage ACCESSES / USER_DATA)
  const [projectNameMap, setProjectNameMap] = useState({});
  // 🔹 dropdown options for filter
  const [projectOptions, setProjectOptions] = useState([]);

  const bgColor = theme === "dark" ? "#191922" : BG_OFFWHITE;
  const cardBg = theme === "dark" ? "#23232c" : "#ffffff";

  // 🔹 1) Load project name map + options from localStorage and API
  useEffect(() => {
    let isMounted = true;

    try {
      let accesses = [];
      const accessesRaw = localStorage.getItem("ACCESSES");
      if (accessesRaw) {
        accesses = JSON.parse(accessesRaw);
      } else {
        const userRaw = localStorage.getItem("USER_DATA");
        if (userRaw) {
          const user = JSON.parse(userRaw);
          if (Array.isArray(user?.accesses)) {
            accesses = user.accesses;
          }
        }
      }

      if (Array.isArray(accesses) && accesses.length) {
        const map = {};
        accesses.forEach((acc) => {
          const pid = acc.project_id || acc.id;
          const name = acc.project_name || acc.name;
          if (pid && name && !/^project\s*#?\s*\d+$/i.test(String(name).trim())) {
            map[String(pid)] = String(name).trim();
          }
        });
        if (Object.keys(map).length > 0) {
          setProjectNameMap((prev) => ({ ...map, ...prev }));
        }
      }
    } catch (e) {
      console.error("Error reading ACCESSES / USER_DATA from localStorage", e);
    }

    // Also fetch real project list for current user
    if (typeof getProjectsForCurrentUser === "function") {
      getProjectsForCurrentUser()
        .then((res) => {
          if (!isMounted) return;
          const pList = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
            ? res
            : res?.data?.results || [];

          if (Array.isArray(pList) && pList.length) {
            setProjectNameMap((prev) => {
              const updated = { ...prev };
              let changed = false;
              pList.forEach((p) => {
                const pid = p?.id || p?.project_id;
                const pName = p?.name || p?.project_name || p?.title;
                if (pid && pName && !/^project\s*#?\s*\d+$/i.test(String(pName).trim())) {
                  const key = String(pid);
                  if (updated[key] !== String(pName).trim()) {
                    updated[key] = String(pName).trim();
                    changed = true;
                  }
                }
              });
              return changed ? updated : prev;
            });
          }
        })
        .catch((err) => {
          console.warn("Could not fetch projects for current user", err);
        });
    }

    return () => {
      isMounted = false;
    };
  }, []);

  // 🔹 Keep projectOptions updated whenever projectNameMap changes
  useEffect(() => {
    const opts = Object.entries(projectNameMap)
      .map(([id, name]) => ({
        id,
        name:
          typeof name === "string" && !/^project\s*#?\s*\d+$/i.test(name.trim())
            ? name.trim()
            : `Project ${id}`,
      }))
      .filter((opt) => opt.name && opt.id)
      .sort((a, b) => a.name.localeCompare(b.name));

    setProjectOptions(opts);
  }, [projectNameMap]);

  // ✅ 2) Load list when docType changes
  useEffect(() => {
    handleClearFilters(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);

  // initial load
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getListApi = () => (docType === "WIR" ? getMyAssignedWIRs : getMyAssignedMIRs);
  const getAcceptApi = () => (docType === "WIR" ? acceptWIR : acceptMIR);
  const getRejectApi = () => (docType === "WIR" ? rejectWIR : rejectMIR);

  const getDocNumber = (row) => {
  if (!row) return "—";
  if (docType === "WIR") return row.inspection_request_no || row.id; // ✅ FIX
  return row.mir_number || row.id;
};
const getDecisionDisplay = (row) => {
  if (!row) return "—";

  // best: backend already sends
  if (row.decision_display) return row.decision_display;

  // fallback: decision + label
  const code = row.decision || "";
  const label = row.decision_label || "";
  if (code && label) return `${code} - ${label}`;

  return code || "—";
};


  const getDocLocation = (row) => row?.location || row?.location_gridlines || row?.zone_area || "—";

  const fetchList = async (extraParams = {}) => {
    try {
      setLoading(true);
      const apiFn = getListApi();
      const res = await apiFn(extraParams);
      const data = res?.data || [];
      const list = Array.isArray(data) ? data : data.results || [];
      setRows(list);

      // Collect project names from rows or missing IDs to fetch
      const missingIds = new Set();
      const newNamesFromRows = {};

      list.forEach((r) => {
        const pid = r.project_id;
        if (!pid) return;
        const key = String(pid);
        const rName =
          (typeof r.project_name === "string" && r.project_name.trim()) ||
          (typeof r.project?.name === "string" && r.project.name.trim()) ||
          "";

        const isSynthetic = !rName || /^project\s*#?\s*\d+$/i.test(rName);
        if (!isSynthetic) {
          newNamesFromRows[key] = rName;
        } else if (!projectNameMap[key] || /^project\s*#?\s*\d+$/i.test(projectNameMap[key])) {
          missingIds.add(Number(pid));
        }
      });

      if (Object.keys(newNamesFromRows).length > 0) {
        setProjectNameMap((prev) => ({ ...prev, ...newNamesFromRows }));
      }

      if (missingIds.size > 0 && projectInstance) {
        try {
          const ids = Array.from(missingIds).filter((id) => id > 0);
          if (ids.length) {
            const batchRes = await projectInstance.post("/projects/by-ids/", { ids });
            const batchData = batchRes?.data?.data || batchRes?.data;
            if (batchData && typeof batchData === "object") {
              const fetchedNames = {};
              Object.entries(batchData).forEach(([pid, p]) => {
                const name = p?.name || p?.project_name || p?.title;
                if (name && typeof name === "string" && !/^project\s*#?\s*\d+$/i.test(name.trim())) {
                  fetchedNames[String(pid)] = name.trim();
                }
              });
              if (Object.keys(fetchedNames).length > 0) {
                setProjectNameMap((prev) => ({ ...prev, ...fetchedNames }));
              }
            }
          }
        } catch (fetchErr) {
          console.warn("Could not batch fetch missing project names", fetchErr);
        }
      }
    } catch (err) {
      console.error("Failed to load inbox", err);
      toast.error(`${docType} inbox load error`);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (id) => {
    if (!id) return;
    if (docType === "WIR") navigate(`/wir/${id}`);
    else navigate(`/mir/${id}`);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    const params = {};
    if (docType !== "WIR" && statusFilter) params.status = statusFilter; // ✅
    if (projectIdFilter) params.project_id = projectIdFilter;
    fetchList(params);
  };

  const handleClearFilters = (silent = false) => {
    setStatusFilter("");
    setProjectIdFilter("");
    setCurrentPage(1);
    if (!silent) fetchList();
    else fetchList(); // still reload, just no toast
  };

  const handleAccept = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Accept ${docType} #${getDocNumber(row)}?`)) return;

    setActionLoadingId(row.id);
    try {
      const acceptFn = getAcceptApi();
      await acceptFn(row.id, { comment: `Accepted via ${docType} inbox.` });
      toast.success(`${docType} acceped`);
      await fetchList();
    } catch (err) {
      console.error("Accept error", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        "Accept error";
      toast.error(msg);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (row) => {
    if (!row?.id) return;
    const reason = window.prompt("Reject reason (optional):", "");
    if (reason === null) return;

    setActionLoadingId(row.id);
    try {
      const rejectFn = getRejectApi();
      await rejectFn(row.id, { comment: reason || `Rejected via ${docType} inbox.` });
      toast.success(`${docType} rejected`);
      await fetchList();
    } catch (err) {
      console.error("Reject error", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        "Reject error";
      toast.error(msg);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleForward = (row) => {
    if (!row?.id) return;
    if (docType === "WIR") navigate(`/wir/${row.id}?mode=forward`);
    else navigate(`/mir/${row.id}?mode=forward`);
  };

  // Helper function for status badges
  const getStatusStyle = (status) => {
    const baseStyle = {
      display: "inline-block",
      padding: "4px 12px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "bold",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    };

    switch (status?.toUpperCase()) {
      case "APPROVED":
      case "ACCEPTED":
        return { ...baseStyle, background: "#d1fae5", color: "#065f46" };
      case "REJECTED":
        return { ...baseStyle, background: "#fee2e2", color: "#991b1b" };
      case "PENDING":
        return { ...baseStyle, background: "#fef9c3", color: "#854d0e" };
      case "UNDER_REVIEW":
        return { ...baseStyle, background: "#e0f2fe", color: "#075985" };
      case "DRAFT":
        return { ...baseStyle, background: "#f3f4f6", color: "#374151" };
      case "CLOSED":
        return { ...baseStyle, background: "#e5e7eb", color: "#1f2937" };
      case "COMPLETED":
        return { ...baseStyle, background: "#d1fae5", color: "#065f46" };

      default:
        return { ...baseStyle, background: "#f3f4f6", color: "#6b7280" };
    }
  };

  // Stats calculation
  const stats =
  docType === "WIR"
    ? { total: rows.length }
    : {
        total: rows.length,
        pending: rows.filter((r) => r.status === "PENDING").length,
        approved: rows.filter((r) => r.status === "APPROVED" || r.status === "ACCEPTED").length,
        rejected: rows.filter((r) => r.status === "REJECTED").length,
      };

  // Reset pagination when docType, status, or project changes
  useEffect(() => {
    setCurrentPage(1);
  }, [docType, statusFilter, projectIdFilter]);

  // Pagination calculations (only paginate if more than 10 items)
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, rows.length);
  const paginatedRows = rows.length > pageSize ? rows.slice(startIndex, endIndex) : rows;

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (safeCurrentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (safeCurrentPage >= totalPages - 3) {
      pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "...", safeCurrentPage - 1, safeCurrentPage, safeCurrentPage + 1, "...", totalPages);
    }
    return pages;
  };

  const headerTitle =
    docType === "WIR" ? "Work Inspection Requests" : "Material Inspection Requests";
  const headerSub =
    docType === "WIR" ? "Manage and review all assigned WIRs" : "Manage and review all assigned MIRs";

  return (
    <div style={{ ...styles.container, background: bgColor }}>
      {/* Header Section */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{headerTitle}</h1>
          <p style={styles.subtitle}>{headerSub}</p>
        </div>

        {/* ✅ Right side: dropdown + create */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            style={{
              ...styles.filterSelect,
              minWidth: 180,
              borderColor: "#e2e8f0",
              background: cardBg,
            }}
            title="Select list type"
          >
            <option value="MIR">MIR List</option>
            <option value="WIR">WIR List</option>
          </select>

          <button
            onClick={() => navigate(docType === "WIR" ? "/wir/create" : "/mir/create")}
            style={styles.createButton}
          >
            + Create New {docType}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
  <div style={{ ...styles.statCard, background: cardBg, borderLeft: `4px solid ${ORANGE}` }}>
    <div style={styles.statLabel}>Total {docType}s</div>
    <div style={styles.statValue}>{stats.total}</div>
  </div>

  {docType !== "WIR" && (
    <>
      <div style={{ ...styles.statCard, background: cardBg, borderLeft: "4px solid #f59e0b" }}>
        <div style={styles.statLabel}>Pending Review</div>
        <div style={styles.statValue}>{stats.pending}</div>
      </div>

      <div style={{ ...styles.statCard, background: cardBg, borderLeft: "4px solid #10b981" }}>
        <div style={styles.statLabel}>Approved</div>
        <div style={styles.statValue}>{stats.approved}</div>
      </div>

      <div style={{ ...styles.statCard, background: cardBg, borderLeft: "4px solid #ef4444" }}>
        <div style={styles.statLabel}>Rejected</div>
        <div style={styles.statValue}>{stats.rejected}</div>
      </div>
    </>
  )}
</div>


      {/* Filters Section */}
      <div style={{ ...styles.filtersCard, background: cardBg }}>
        <div style={styles.filtersHeader}>
          <h3 style={styles.filtersTitle}> Filters</h3>
          {(statusFilter || projectIdFilter) && (
            <button type="button" onClick={() => handleClearFilters(false)} style={styles.clearButton}>
              ✕ Clear All
            </button>
          )}
        </div>

        <div style={styles.filtersGrid}>
          {/* Status Filter */}
          {docType !== "WIR" && (
  <div style={styles.filterGroup}>
    <label style={styles.filterLabel}>Status</label>
    <select
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      style={styles.filterSelect}
    >
      <option value="">All Statuses</option>
      <option value="DRAFT">Draft</option>
      <option value="PENDING">Pending</option>
      <option value="UNDER_REVIEW">Under Review</option>
      <option value="APPROVED">Approved</option>
      <option value="REJECTED">Rejected</option>
      <option value="CLOSED">Closed</option>
    </select>
  </div>
)}


          {/* Project Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Project</label>
            <select
              value={projectIdFilter}
              onChange={(e) => setProjectIdFilter(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Projects</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>&nbsp;</label>
            <button type="button" onClick={handleApplyFilters} style={styles.applyButton}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div style={{ ...styles.tableCard, background: cardBg }}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading {docType}s...</p>
          </div>
        ) : rows.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📭</div>
            <h3 style={styles.emptyTitle}>No {docType}s Found</h3>
            <p style={styles.emptyText}>
              {statusFilter || projectIdFilter ? "Try adjusting your filters" : `No ${docType}s are currently assigned to you`}
            </p>
          </div>
        ) : (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.th}>{docType} Number</th>
                    <th style={styles.th}>Project</th>
                    <th style={styles.th}>Location</th>
  {docType !== "WIR" && <th style={styles.th}>Status</th>}   {/* ✅ only MIR */}

  {docType === "WIR" && <th style={styles.th}>Decision</th>}
                    {/* {docType === "WIR" && <th style={styles.th}>Decision</th>} ✅ NEW */}

                    <th style={styles.th}>Created By</th>
                    <th style={styles.th}>Created Date</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row, index) => {
                    const rawProjName =
                      row.project_name ||
                      row.project?.name ||
                      projectNameMap[String(row.project_id)] ||
                      "";

                    const cleanProjName =
                      typeof rawProjName === "string" && !/^project\s*#?\s*\d+$/i.test(rawProjName.trim())
                        ? rawProjName.trim()
                        : projectNameMap[String(row.project_id)] &&
                          !/^project\s*#?\s*\d+$/i.test(projectNameMap[String(row.project_id)].trim())
                        ? projectNameMap[String(row.project_id)].trim()
                        : null;

                    const displayProj = cleanProjName || (row.project_id ? `Project ${row.project_id}` : "—");

                    return (
                      <tr
                        key={row.id}
                        style={{
                          ...styles.tableRow,
                          background: index % 2 === 0 ? "#ffffff" : "#f9fafb",
                        }}
                      >
                        <td style={styles.td}>
                          <div style={styles.mirNumber}>#{getDocNumber(row)}</div>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.projectBadge}>
                            {displayProj}
                          </div>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.location}>{getDocLocation(row)}</div>
                        </td>

                        

  {docType !== "WIR" ? (
    <td style={styles.td}>
      <span style={getStatusStyle(row.status)}>{row.status || "N/A"}</span>
    </td>
  ) : (
    <td style={styles.td}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
        {getDecisionDisplay(row)}
      </div>
    </td>
  )}


                        <td style={styles.td}>
                          <div style={styles.createdBy}>{row.created_by_name || "—"}</div>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.date}>
                            {row.created_at
                              ? new Date(row.created_at).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "—"}
                          </div>
                          <div style={styles.time}>
                            {row.created_at
                              ? new Date(row.created_at).toLocaleTimeString("en-IN", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : ""}
                          </div>
                        </td>

                        <td style={styles.td}>
    <div style={styles.actionsContainer}>
      <button
        type="button"
        onClick={() => handleView(row.id)}
        style={styles.viewButton}
      >
        View
      </button>

      {docType !== "WIR" && (
        <>
          <button
            type="button"
            onClick={() => handleAccept(row)}
            disabled={actionLoadingId === row.id}
            style={styles.acceptButton}
          >
            {actionLoadingId === row.id ? "..." : "✓ Accept"}
          </button>

          <button
            type="button"
            onClick={() => handleReject(row)}
            disabled={actionLoadingId === row.id}
            style={styles.rejectButton}
          >
            {actionLoadingId === row.id ? "..." : "✕ Reject"}
          </button>

          <button
            type="button"
            onClick={() => handleForward(row)}
            style={styles.forwardButton}
          >
            Forward
          </button>
        </>
      )}
    </div>
  </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls - Only shown if more than 10 points */}
            {rows.length > pageSize && (
              <div
                style={{
                  ...styles.paginationContainer,
                  borderTop: theme === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
                }}
              >
                <p style={styles.paginationInfo}>
                  Showing <strong>{startIndex + 1}</strong> to <strong>{endIndex}</strong> of{" "}
                  <strong>{rows.length}</strong> {docType}s
                </p>

                <div style={styles.paginationButtons}>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safeCurrentPage === 1}
                    style={{
                      ...styles.pageButton,
                      ...(safeCurrentPage === 1 ? styles.pageButtonDisabled : {}),
                      background: cardBg,
                      color: theme === "dark" ? "#cbd5e1" : "#334155",
                      borderColor: theme === "dark" ? "#334155" : "#e2e8f0",
                    }}
                    title="Previous Page"
                  >
                    ‹ Prev
                  </button>

                  {getPageNumbers().map((pageItem, idx) => {
                    if (pageItem === "...") {
                      return (
                        <span
                          key={`ellipsis-${idx}`}
                          style={{
                            padding: "0 6px",
                            fontSize: "13px",
                            color: "#94a3b8",
                            userSelect: "none",
                          }}
                        >
                          ...
                        </span>
                      );
                    }

                    const isActive = pageItem === safeCurrentPage;
                    return (
                      <button
                        key={pageItem}
                        type="button"
                        onClick={() => setCurrentPage(pageItem)}
                        style={{
                          ...styles.pageButton,
                          ...(isActive
                            ? styles.pageButtonActive
                            : {
                                background: cardBg,
                                color: theme === "dark" ? "#cbd5e1" : "#334155",
                                borderColor: theme === "dark" ? "#334155" : "#e2e8f0",
                              }),
                        }}
                      >
                        {pageItem}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safeCurrentPage === totalPages}
                    style={{
                      ...styles.pageButton,
                      ...(safeCurrentPage === totalPages ? styles.pageButtonDisabled : {}),
                      background: cardBg,
                      color: theme === "dark" ? "#cbd5e1" : "#334155",
                      borderColor: theme === "dark" ? "#334155" : "#e2e8f0",
                    }}
                    title="Next Page"
                  >
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer Info (shown when 10 or fewer points) */}
      {!loading && rows.length > 0 && rows.length <= pageSize && (
        <div style={styles.footer}>
          <p style={styles.footerText}>
            Showing <strong>{rows.length}</strong> {docType}
            {rows.length !== 1 ? "s" : ""}
          </p>
        </div>
      )}
    </div>
  );
}

// STYLES OBJECT (same as your current)
const styles = {
  container: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "24px",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background: "#f8fafc",
    minHeight: "100vh",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },

  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e293b",
    margin: 0,
    marginBottom: "4px",
  },

  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
  },

  createButton: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #ffbe63 0%, #ff9f1c 100%)",
    color: "black",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(248, 180, 80, 0.5)",
    transition: "all 0.2s ease",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },

  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },

  statLabel: {
    fontSize: "13px",
    color: "#64748b",
    fontWeight: "500",
    marginBottom: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  statValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#1e293b",
  },

  filtersCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    marginBottom: "24px",
  },

  filtersHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },

  filtersTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
    margin: 0,
  },

  clearButton: {
    padding: "6px 12px",
    background: "#fee2e2",
    color: "#991b1b",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    alignItems: "end",
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
  },

  filterLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
  },

  filterSelect: {
    padding: "10px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1e293b",
    background: "white",
    cursor: "pointer",
    transition: "border-color 0.2s ease",
  },

  applyButton: {
    padding: "10px 20px",
    background: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  tableCard: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  },

  tableWrapper: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },

  tableHeaderRow: {
    background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
    borderBottom: "2px solid #e2e8f0",
  },

  th: {
    textAlign: "left",
    padding: "16px 12px",
    fontWeight: "600",
    color: "#475569",
    fontSize: "13px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
  },

  tableRow: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.2s ease",
  },

  td: {
    padding: "16px 12px",
    verticalAlign: "middle",
  },

  mirNumber: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#1e293b",
  },

  projectBadge: {
    display: "inline-block",
    padding: "4px 10px",
    background: "#dbeafe",
    color: "#1e40af",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
  },

  location: {
    fontSize: "14px",
    color: "#64748b",
  },

  createdBy: {
    fontSize: "14px",
    color: "#475569",
    fontWeight: "500",
  },

  date: {
    fontSize: "14px",
    color: "#1e293b",
    fontWeight: "500",
  },

  time: {
    fontSize: "12px",
    color: "#94a3b8",
    marginTop: "2px",
  },

  actionsContainer: {
    display: "flex",
    gap: "6px",
    flexWrap: "nowrap",
  },

  viewButton: {
    padding: "6px 12px",
    background: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },

  acceptButton: {
    padding: "6px 12px",
    background: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },

  rejectButton: {
    padding: "6px 12px",
    background: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },

  forwardButton: {
    padding: "6px 12px",
    background: ORANGE,
    color: "#111827",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },

  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
  },

  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f1f5f9",
    borderTop: `4px solid ${ORANGE}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  loadingText: {
    marginTop: "16px",
    fontSize: "14px",
    color: "#64748b",
    fontWeight: "500",
  },

  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
  },

  emptyIcon: {
    fontSize: "64px",
    marginBottom: "16px",
  },

  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "8px",
  },

  emptyText: {
    fontSize: "14px",
    color: "#64748b",
  },

  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    padding: "14px 20px",
  },

  paginationInfo: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
  },

  paginationButtons: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  },

  pageButton: {
    minWidth: "34px",
    height: "34px",
    padding: "0 10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },

  pageButtonActive: {
    background: "linear-gradient(135deg, #ffbe63 0%, #ff9f1c 100%)",
    color: "#1e293b",
    borderColor: "#ffbe63",
    boxShadow: "0 2px 6px rgba(255, 190, 99, 0.4)",
  },

  pageButtonDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },

  footer: {
    marginTop: "20px",
    textAlign: "center",
  },

  footerText: {
    fontSize: "13px",
    color: "#64748b",
  },
};

// spinner animation
const styleSheet = document.styleSheets[0];
if (styleSheet) {
  try {
    styleSheet.insertRule(
      `@keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }`,
      styleSheet.cssRules.length
    );
  } catch (e) {}
}